/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { Color, CylinderBufferGeometry, LinearFilter, Matrix4, Mesh, MeshBasicMaterial, Object3D, Quaternion, Sprite, SpriteMaterial, Texture, Vector3, Vector2, } from 'three';
import { FunctionalActor } from '../../actors/FunctionalActor';
import { ObjectInteractor } from '../ObjectInteractor';
const SCENE_COMPASS_DEFAULT_CONFIG = {
    interactive: true,
    leftAbs: 30,
    bottomAbs: 30,
    widthAbs: 100,
    heightAbs: 100,
};
const TEXTURE_SIZE = 128;
const TEXTURE_ANISOTROPHY = 2;
const AXIS_SPHERE_SIZE = 0.6;
const AXIS_SPHERE_OFFSET = 1.25;
const LINE_RADIUS = .042;
const LINE_LENGTH = 1;
const LINE_OFFSET = LINE_LENGTH / 2;
const LINE_SEGMENTS = 5;
const DARK_FACTOR = .5;
const DARK_TEXT_FACTOR = .2;
const LIGHT = new Color(0xffffff);
const DARK = LIGHT.clone().multiplyScalar(DARK_FACTOR);
const RED = new Color('#fd3352');
const GREEN = new Color('#8ad901');
const BLUE = new Color('#2890ff');
const WHITE = new Color('#f0f0f0');
const BLACK = new Color('#303030');
const RED_DARK = RED.clone().multiplyScalar(DARK_FACTOR);
const GREEN_DARK = GREEN.clone().multiplyScalar(DARK_FACTOR);
const BLUE_DARK = BLUE.clone().multiplyScalar(DARK_FACTOR);
const toCssColor = (col) => `#${col.getHexString()}`;
function makeTexture(image, anisotropy = 0) {
    const texture = new Texture(image);
    texture.flipY = true;
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;
    texture.repeat.set(.5, 1);
    if (anisotropy) {
        texture.anisotropy = anisotropy;
    }
    texture.needsUpdate = true;
    return texture;
}
function createAxisSphereCanvas(size, sphereColor, text, textColor) {
    const canvas = document.createElement('canvas');
    canvas.width = size * 2;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    // sphere
    ctx.beginPath();
    const halfSize = size / 2;
    ctx.arc(halfSize, halfSize, halfSize, 0, 2 * Math.PI);
    ctx.fillStyle = toCssColor(sphereColor[0]);
    ctx.fill();
    // highlighted sphere
    ctx.beginPath();
    ctx.arc(size + halfSize, halfSize, halfSize, 0, 2 * Math.PI);
    ctx.fillStyle = toCssColor(sphereColor[1]);
    ctx.fill();
    if (text) {
        const measure = ctx.measureText(text);
        ctx.font = `bold ${Math.round(size * 0.9)}px sans-serif`;
        // label
        ctx.fillStyle = toCssColor(textColor[0]);
        ctx.fillText(text, (size - measure.width) * 0.25, size * 0.825, size);
        // highlighted label
        ctx.fillStyle = toCssColor(textColor[1]);
        ctx.fillText(text, size + ((size - measure.width) * 0.25), size * 0.825, size);
    }
    return canvas;
}
function createAxisSphereSprite(name, map, position) {
    const material = new SpriteMaterial({ map, color: 0xffffff });
    const sprite = new Sprite(material);
    sprite.scale.setScalar(AXIS_SPHERE_SIZE);
    sprite.position.copy(position);
    sprite.name = name;
    return sprite;
}
function createAxisLine(name, color, translate, rotAxis) {
    const geometry = new CylinderBufferGeometry(LINE_RADIUS, LINE_RADIUS, LINE_LENGTH, LINE_SEGMENTS);
    const transform = new Matrix4();
    transform.makeTranslation(translate.x, translate.y, translate.z);
    if (rotAxis) {
        const rotate = new Matrix4();
        rotate.makeRotationAxis(rotAxis, Math.PI * 0.5);
        transform.multiply(rotate);
    }
    geometry.applyMatrix(transform);
    const material = new MeshBasicMaterial({ color });
    const mesh = new Mesh(geometry, material);
    mesh.name = name;
    return mesh;
}
const updateSpriteTextureOffset = (offset) => (sprite) => {
    const { material } = sprite;
    material.map.offset.copy(offset);
    material.needsUpdate = true;
};
const activateHighlightTexture = updateSpriteTextureOffset(new Vector2(.5, 0));
const activateUnhighlightTexture = updateSpriteTextureOffset(new Vector2(0, 0));
const updateColor = ({ material, userData }, color, overColor = color) => {
    material.color = userData.isOver ? overColor : color;
};
const updateIsOver = ({ userData }, isOver) => {
    userData.isOver = isOver;
};
const updateCamera = (cam, fn) => {
    const dist = cam.position.distanceTo(new Vector3());
    const { up, position } = fn(dist);
    cam.rotation.set(0, 0, 0);
    cam.up.copy(up);
    cam.position.copy(position);
    cam.lookAt(0, 0, 0);
    cam.userData.pivot = new Vector3();
};
/**
 * Represents a visual axis object.
 */
export class SceneCompass extends Object3D {
    constructor(options) {
        super();
        this._isHidden = false;
        this.controlCamera = options && options.controlCamera;
        if (options && Array.isArray(options.sceneUp)) {
            const sceneUp = new Vector3(...options.sceneUp).normalize();
            this.sceneUpPostQuaternion = new Quaternion();
            this.sceneUpPostQuaternion.setFromUnitVectors(new Vector3(0, 1, 0), sceneUp);
        }
        const xFrontAxis = createAxisLine('SceneCompass.xFrontAxis', RED.getHex(), new Vector3(LINE_OFFSET, 0, 0), new Vector3(0, 0, 1));
        const yFrontAxis = createAxisLine('SceneCompass.yFrontAxis', GREEN.getHex(), new Vector3(0, LINE_OFFSET, 0));
        const zFrontAxis = createAxisLine('SceneCompass.zFrontAxis', BLUE.getHex(), new Vector3(0, 0, LINE_OFFSET), new Vector3(1, 0, 0));
        const sphereTexSize = (options && options.sphereTextureSize) || TEXTURE_SIZE;
        const xFrontSphere = createAxisSphereSprite('SceneCompass.xFrontSphere', makeTexture(createAxisSphereCanvas(sphereTexSize, [RED, WHITE], 'X', [RED.clone().multiplyScalar(DARK_TEXT_FACTOR), BLACK]), TEXTURE_ANISOTROPHY), new Vector3(AXIS_SPHERE_OFFSET, 0, 0));
        xFrontSphere.addEventListener(ObjectInteractor.HIGHLIGHT, () => {
            activateHighlightTexture(xFrontSphere);
            updateIsOver(xFrontAxis, true);
        });
        xFrontSphere.addEventListener(ObjectInteractor.UNHIGHLIGHT, () => {
            activateUnhighlightTexture(xFrontSphere);
            updateIsOver(xFrontAxis, false);
        });
        const postTransform = (up) => this.sceneUpPostQuaternion ? up.applyQuaternion(this.sceneUpPostQuaternion) : up;
        xFrontSphere.addEventListener(ObjectInteractor.CLICK, () => {
            updateCamera(this.controlCamera, (d) => ({
                up: postTransform(new Vector3(0, 1, 0)),
                position: new Vector3(d, 0, 0),
            }));
        });
        const yFrontSphere = createAxisSphereSprite('SceneCompass.yFrontSphere', makeTexture(createAxisSphereCanvas(sphereTexSize, [GREEN, WHITE], 'Y', [GREEN.clone().multiplyScalar(DARK_TEXT_FACTOR), BLACK]), TEXTURE_ANISOTROPHY), new Vector3(0, AXIS_SPHERE_OFFSET, 0));
        yFrontSphere.addEventListener(ObjectInteractor.HIGHLIGHT, () => {
            activateHighlightTexture(yFrontSphere);
            updateIsOver(yFrontAxis, true);
        });
        yFrontSphere.addEventListener(ObjectInteractor.UNHIGHLIGHT, () => {
            activateUnhighlightTexture(yFrontSphere);
            updateIsOver(yFrontAxis, false);
        });
        yFrontSphere.addEventListener(ObjectInteractor.CLICK, () => {
            updateCamera(this.controlCamera, (d) => ({
                up: postTransform(new Vector3(0, 0, 1)),
                position: new Vector3(0, d, 0),
            }));
        });
        const zFrontSphere = createAxisSphereSprite('SceneCompass.zFrontSphere', makeTexture(createAxisSphereCanvas(sphereTexSize, [BLUE, WHITE], 'Z', [BLUE.clone().multiplyScalar(DARK_TEXT_FACTOR), BLACK]), TEXTURE_ANISOTROPHY), new Vector3(0, 0, AXIS_SPHERE_OFFSET));
        zFrontSphere.addEventListener(ObjectInteractor.HIGHLIGHT, () => {
            activateHighlightTexture(zFrontSphere);
            updateIsOver(zFrontAxis, true);
        });
        zFrontSphere.addEventListener(ObjectInteractor.UNHIGHLIGHT, () => {
            activateUnhighlightTexture(zFrontSphere);
            updateIsOver(zFrontAxis, false);
        });
        zFrontSphere.addEventListener(ObjectInteractor.CLICK, () => {
            updateCamera(this.controlCamera, (d) => ({
                up: postTransform(new Vector3(0, 1, 0)),
                position: new Vector3(0, 0, d),
            }));
        });
        const xBackSphere = createAxisSphereSprite('SceneCompass.xBackSphere', makeTexture(createAxisSphereCanvas(sphereTexSize, [RED, WHITE]), TEXTURE_ANISOTROPHY), new Vector3(-AXIS_SPHERE_OFFSET, 0, 0));
        xBackSphere.addEventListener(ObjectInteractor.HIGHLIGHT, () => activateHighlightTexture(xBackSphere));
        xBackSphere.addEventListener(ObjectInteractor.UNHIGHLIGHT, () => activateUnhighlightTexture(xBackSphere));
        xBackSphere.addEventListener(ObjectInteractor.CLICK, () => {
            updateCamera(this.controlCamera, (d) => ({
                up: postTransform(new Vector3(0, 1, 0)),
                position: new Vector3(-d, 0, 0),
            }));
        });
        const yBackSphere = createAxisSphereSprite('SceneCompass.yBackSphere', makeTexture(createAxisSphereCanvas(sphereTexSize, [GREEN, WHITE]), TEXTURE_ANISOTROPHY), new Vector3(0, -AXIS_SPHERE_OFFSET, 0));
        yBackSphere.addEventListener(ObjectInteractor.HIGHLIGHT, () => activateHighlightTexture(yBackSphere));
        yBackSphere.addEventListener(ObjectInteractor.UNHIGHLIGHT, () => activateUnhighlightTexture(yBackSphere));
        yBackSphere.addEventListener(ObjectInteractor.CLICK, () => {
            updateCamera(this.controlCamera, (d) => ({
                up: postTransform(new Vector3(0, 0, 1)),
                position: new Vector3(0, -d, 0),
            }));
        });
        const zBackSphere = createAxisSphereSprite('SceneCompass.zBackSphere', makeTexture(createAxisSphereCanvas(sphereTexSize, [BLUE, WHITE]), TEXTURE_ANISOTROPHY), new Vector3(0, 0, -AXIS_SPHERE_OFFSET));
        zBackSphere.addEventListener(ObjectInteractor.HIGHLIGHT, () => activateHighlightTexture(zBackSphere));
        zBackSphere.addEventListener(ObjectInteractor.UNHIGHLIGHT, () => activateUnhighlightTexture(zBackSphere));
        zBackSphere.addEventListener(ObjectInteractor.CLICK, () => {
            updateCamera(this.controlCamera, (d) => ({
                up: postTransform(new Vector3(0, 1, 0)),
                position: new Vector3(0, 0, -d),
            }));
        });
        this.add(xFrontSphere, yFrontSphere, zFrontSphere, xFrontAxis, yFrontAxis, zFrontAxis, xBackSphere, yBackSphere, zBackSphere);
        const interactor = options && options.interactor;
        if (interactor) {
            this._interactor = interactor;
            interactor.addInteractiveObject(xFrontSphere, yFrontSphere, zFrontSphere, xBackSphere, yBackSphere, zBackSphere);
        }
        Object.assign(this, {
            xFrontAxis,
            yFrontAxis,
            zFrontAxis,
            xFrontSphere,
            yFrontSphere,
            zFrontSphere,
            xBackSphere,
            yBackSphere,
            zBackSphere,
        });
    }
    /**
     * Attach a scene compass overlay to a viewport
     * @param stage the stage
     * @param targetViewport the name of the target viewport
     * @param mirrorCamera the name of the camera to mirror
     */
    static create(stage, targetViewport, mirrorCamera, options = SCENE_COMPASS_DEFAULT_CONFIG) {
        const cameraName = `scenecompass:${targetViewport}:camera`;
        const sceneName = `scenecompass:${targetViewport}:scene`;
        const viewportName = `scenecompass:${targetViewport}:viewport`;
        const navigatorName = `scenecompass:${targetViewport}:navigator`;
        const vpDesc = Object.assign({}, options, { parent: targetViewport, name: viewportName, scene: sceneName, camera: cameraName, navigator: navigatorName, bgcolor: 0, noclear: true });
        stage.setupStage({
            cameras: [{
                    name: cameraName,
                    from: [0, 0, 4],
                    to: [0, 0, 0],
                    up: [0, 1, 0],
                    fov: 45,
                }],
            scenes: [{ name: sceneName }],
            viewports: [vpDesc],
        });
        // tslint:disable-next-line:no-console
        // console.log('mirror.up', stage.getCameraDesc(mirrorCamera).up);
        const sceneUp = stage.getCameraDesc(mirrorCamera).up;
        const sceneCompass = new SceneCompass(options && options.interactive ? {
            interactor: new ObjectInteractor(stage, cameraName, viewportName),
            controlCamera: stage.getCamera(mirrorCamera),
            sceneUp,
        } : {
            sceneUp,
        });
        stage.applyThreeDirect((scene) => {
            scene.add(sceneCompass);
        }, sceneName);
        sceneCompass.mirrorCamera(stage, mirrorCamera);
        return sceneCompass;
    }
    get isHidden() {
        return this._isHidden;
    }
    set isHidden(hide) {
        this.toggleVisibility(!hide);
    }
    hide() {
        this.visible = false;
        if (this._interactor) {
            this._interactor.pause(true);
        }
        this._isHidden = true;
    }
    show() {
        this.visible = true;
        if (this._interactor) {
            this._interactor.pause(false);
        }
        this._isHidden = false;
    }
    toggleVisibility(visible = this._isHidden) {
        if (visible) {
            this.show();
        }
        else {
            this.hide();
        }
    }
    pauseInteraction(pause) {
        this._interactor.pause(pause);
    }
    /**
     * Connect the axis object with the camera to be mirrored.
     * Internally a stage actor will be created which updates the axis object on every frame.
     */
    mirrorCamera(stage, cameraName) {
        const mirrorCamera = stage.getCamera(cameraName);
        if (this._interactor) {
            this._interactor.setExtraEventPayload({ mirrorCamera });
        }
        const actor = this.createMirrorCameraActor(mirrorCamera);
        stage.addActor(actor);
        return actor;
    }
    createMirrorCameraActor(camera) {
        return new FunctionalActor(() => this.mirrorCameraOrientation(camera));
    }
    mirrorCameraOrientation(camera) {
        this.setRotationFromMatrix(camera.matrixWorldInverse);
        this.shadeFrontBackAxes(camera);
    }
    createShadeFrontBackAxesActor(camera) {
        return new FunctionalActor(() => this.shadeFrontBackAxes(camera));
    }
    shadeFrontBackAxes(camera) {
        const q = new Quaternion();
        q.setFromRotationMatrix(camera.matrixWorldInverse);
        // x-axis
        const v = new Vector3(1, 0, 0);
        v.applyQuaternion(q);
        if (v.z >= 0) {
            updateColor(this.xFrontSphere, LIGHT);
            updateColor(this.xFrontAxis, RED, WHITE);
            updateColor(this.xBackSphere, DARK, LIGHT);
        }
        else {
            updateColor(this.xFrontSphere, DARK, LIGHT);
            updateColor(this.xFrontAxis, RED_DARK, WHITE);
            updateColor(this.xBackSphere, LIGHT);
        }
        // y-axis
        v.set(0, 1, 0);
        v.applyQuaternion(q);
        if (v.z >= 0) {
            updateColor(this.yFrontSphere, LIGHT);
            updateColor(this.yFrontAxis, GREEN, WHITE);
            updateColor(this.yBackSphere, DARK, LIGHT);
        }
        else {
            updateColor(this.yFrontSphere, DARK, LIGHT);
            updateColor(this.yFrontAxis, GREEN_DARK, WHITE);
            updateColor(this.yBackSphere, LIGHT);
        }
        // z-axis
        v.set(0, 0, 1);
        v.applyQuaternion(q);
        if (v.z >= 0) {
            updateColor(this.zFrontSphere, LIGHT);
            updateColor(this.zFrontAxis, BLUE, WHITE);
            updateColor(this.zBackSphere, DARK, LIGHT);
        }
        else {
            updateColor(this.zFrontSphere, DARK, LIGHT);
            updateColor(this.zFrontAxis, BLUE_DARK, WHITE);
            updateColor(this.zBackSphere, LIGHT);
        }
    }
}
//# sourceMappingURL=SceneCompass.js.map